/**
 * 📊 Affiliate Mock Data Layer
 * Centralized mock data for the Affiliate Portal
 * 
 * This will be replaced with real API calls in production.
 * Keep data structures aligned with expected backend DTOs.
 */

import type { EarningsDataPoint } from '@/components/affiliate/charts/EarningsChart';

// ============================================
// TYPE DEFINITIONS
// ============================================

// Lead Types
export type LeadStatus = 'neu' | 'kontaktiert' | 'interessiert' | 'verhandlung' | 'gewonnen' | 'verloren';
export type LeadSource = 'website' | 'referral' | 'cold-call' | 'event' | 'social';

export interface Lead {
  id: number;
  restaurant: string;
  contact: string;
  phone: string;
  email: string;
  status: LeadStatus;
  source: LeadSource;
  createdAt: string;
  lastContact?: string;
  notes?: string;
}

// Commission Types
export type CommissionType = 'einmalig' | 'wiederkehrend' | 'bonus';
export type CommissionStatus = 'ausstehend' | 'freigegeben' | 'ausgezahlt';

export interface Commission {
  id: number;
  restaurant: string;
  type: CommissionType;
  amount: number;
  status: CommissionStatus;
  date: string;
  payoutDate?: string;
}

// Payout Types
export type PayoutStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface Payout {
  id: number;
  date: string;
  amount: number;
  status: PayoutStatus;
  method: string;
  reference: string;
  invoiceAvailable: boolean;
}

// Restaurant Types
export type RestaurantStatus = 'aktiv' | 'inaktiv' | 'pending';

export interface Restaurant {
  id: number;
  name: string;
  address: string;
  city: string;
  status: RestaurantStatus;
  monthlyRevenue: number;
  commission: number;
  joinedDate: string;
  lastOrder: string;
}

// Level Types
export interface Level {
  level: number;
  name: string;
  minPoints: number;
  maxPoints: number;
  bonus: number;
  perks: string[];
  unlocked: boolean;
}

// Activity Types
export type ActivityType = 'lead' | 'conversion' | 'payout' | 'level' | 'commission';

export interface Activity {
  id: number;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  amount?: number;
}

// Marketing Asset Types
export interface MarketingAsset {
  id: number;
  name: string;
  format: string;
  size?: string;
  duration?: string;
  category: 'banner' | 'document' | 'template' | 'video';
}

// Profile Types
export interface AffiliateProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  vatId: string;
  street: string;
  city: string;
  zip: string;
  country: string;
  referralCode: string;
}

// Payment Settings
export interface PaymentSettings {
  method: 'sepa' | 'paypal';
  iban: string;
  bic: string;
  accountHolder: string;
  paypalEmail: string;
  minPayout: string;
}

// Notification Settings
export interface NotificationSettings {
  emailNewLead: boolean;
  emailConversion: boolean;
  emailPayout: boolean;
  emailNewsletter: boolean;
  pushEnabled: boolean;
  pushNewLead: boolean;
  pushConversion: boolean;
}

// ============================================
// DASHBOARD MOCK DATA
// ============================================

export const MOCK_KPI_DATA = {
  totalEarnings: 5340.20,
  monthlyEarnings: 942.54,
  activeRestaurants: 12,
  conversionRate: 67,
};

export const MOCK_QUICK_STATS = [
  { label: 'Offene Leads', value: 8 },
  { label: 'Ausstehende Provision', value: '€358,10' },
  { label: 'Nächste Auszahlung', value: '05. Apr' },
];

export const MOCK_KONSTANTLEITER = {
  currentLevel: 3,
  currentLevelName: 'Silber',
  progress: 73,
  pointsToNext: 134,
};

export const MOCK_ACTIVITIES: Activity[] = [
  { id: 1, type: 'lead', title: 'Neuer Lead', description: 'Pizzeria Milano wurde erfasst', timestamp: 'vor 2h' },
  { id: 2, type: 'conversion', title: 'Conversion!', description: 'Pizza Palace ist jetzt aktiv', timestamp: 'vor 1 Tag', amount: 250 },
  { id: 3, type: 'payout', title: 'Auszahlung', description: 'März-Provision überwiesen', timestamp: 'vor 3 Tagen', amount: 396.90 },
  { id: 4, type: 'commission', title: 'Provision', description: 'Burger House Monatsprovision', timestamp: 'vor 5 Tagen', amount: 62.30 },
  { id: 5, type: 'level', title: 'Level Up!', description: 'Du hast Level 3 erreicht', timestamp: 'vor 1 Woche' },
];

export const MOCK_REFERRAL_LINK = 'speiseguru.de/ref/martink';

export const MOCK_EARNINGS_CHART: EarningsDataPoint[] = [
  { month: 'Okt', earnings: 8200, commissions: 82 },
  { month: 'Nov', earnings: 9500, commissions: 95 },
  { month: 'Dez', earnings: 12800, commissions: 128 },
  { month: 'Jan', earnings: 11200, commissions: 362 },
  { month: 'Feb', earnings: 14500, commissions: 395 },
  { month: 'Mär', earnings: 16800, commissions: 418 },
];

// ============================================
// LEADS MOCK DATA
// ============================================

export const MOCK_LEADS: Lead[] = [
  { id: 1, restaurant: 'Pizzeria Milano', contact: 'Marco Rossi', phone: '+49 172 1234567', email: 'marco@pizzeriamilano.de', status: 'neu', source: 'website', createdAt: '2024-03-28' },
  { id: 2, restaurant: 'Asia House', contact: 'Li Wei', phone: '+49 173 2345678', email: 'info@asiahouse.de', status: 'kontaktiert', source: 'cold-call', createdAt: '2024-03-25', lastContact: '2024-03-27' },
  { id: 3, restaurant: 'Burger Bros', contact: 'Tim Schmidt', phone: '+49 174 3456789', email: 'tim@burgerbros.de', status: 'interessiert', source: 'referral', createdAt: '2024-03-20', lastContact: '2024-03-26' },
  { id: 4, restaurant: 'Griechische Taverne', contact: 'Nikos Papadopoulos', phone: '+49 175 4567890', email: 'nikos@taverne.de', status: 'verhandlung', source: 'event', createdAt: '2024-03-15', lastContact: '2024-03-28' },
  { id: 5, restaurant: 'Pizza Palace', contact: 'Giovanni Bianchi', phone: '+49 176 5678901', email: 'giovanni@pizzapalace.de', status: 'gewonnen', source: 'website', createdAt: '2024-01-10', lastContact: '2024-01-15' },
  { id: 6, restaurant: 'Döner King', contact: 'Mehmet Yilmaz', phone: '+49 177 6789012', email: 'mehmet@doenerking.de', status: 'verloren', source: 'cold-call', createdAt: '2024-02-20', lastContact: '2024-03-01' },
];

export const MOCK_LEAD_STATS = {
  total: 24,
  openLeads: 8,
  wonThisMonth: 3,
  conversionRate: 67,
};

// ============================================
// COMMISSIONS MOCK DATA
// ============================================

export const MOCK_COMMISSIONS: Commission[] = [
  { id: 1, restaurant: 'Pizza Palace', type: 'einmalig', amount: 250, status: 'freigegeben', date: '2024-04-01' },
  { id: 2, restaurant: 'Pizza Palace', type: 'wiederkehrend', amount: 45.80, status: 'ausstehend', date: '2024-04-01' },
  { id: 3, restaurant: 'Burger House', type: 'einmalig', amount: 250, status: 'ausgezahlt', date: '2024-03-15', payoutDate: '2024-03-31' },
  { id: 4, restaurant: 'Burger House', type: 'wiederkehrend', amount: 62.30, status: 'ausstehend', date: '2024-04-01' },
  { id: 5, restaurant: 'Level 3 Bonus', type: 'bonus', amount: 500, status: 'freigegeben', date: '2024-03-20' },
  { id: 6, restaurant: 'Sushi World', type: 'einmalig', amount: 250, status: 'ausstehend', date: '2024-03-28' },
];

export const MOCK_COMMISSION_SUMMARY = {
  totalEarned: 5340.20,
  pendingAmount: 358.10,
  releasedAmount: 750.00,
  paidOutAmount: 312.30,
};

// ============================================
// PAYOUTS MOCK DATA
// ============================================

export const MOCK_PAYOUTS: Payout[] = [
  { id: 1, date: '2024-04-01', amount: 942.54, status: 'pending', method: 'SEPA', reference: 'PAY-2024-0041', invoiceAvailable: false },
  { id: 2, date: '2024-03-01', amount: 396.90, status: 'completed', method: 'SEPA', reference: 'PAY-2024-0032', invoiceAvailable: true },
  { id: 3, date: '2024-02-01', amount: 512.30, status: 'completed', method: 'SEPA', reference: 'PAY-2024-0019', invoiceAvailable: true },
  { id: 4, date: '2024-01-01', amount: 289.45, status: 'completed', method: 'PayPal', reference: 'PAY-2024-0008', invoiceAvailable: true },
  { id: 5, date: '2023-12-01', amount: 685.90, status: 'completed', method: 'SEPA', reference: 'PAY-2023-0156', invoiceAvailable: true },
  { id: 6, date: '2023-11-01', amount: 203.10, status: 'failed', method: 'SEPA', reference: 'PAY-2023-0142', invoiceAvailable: false },
];

export const MOCK_PAYOUT_SUMMARY = {
  pendingPayout: 942.54,
  nextPayoutDate: '05. April 2024',
  lifetimePaid: 2088.10,
  paymentMethod: 'SEPA Überweisung',
  iban: 'DE89 •••• •••• •••• 1234',
};

// ============================================
// RESTAURANTS MOCK DATA
// ============================================

export const MOCK_RESTAURANTS: Restaurant[] = [
  { id: 1, name: 'Pizza Palace', address: 'Hauptstraße 123', city: 'Köln', status: 'aktiv', monthlyRevenue: 4580, commission: 45.80, joinedDate: '2024-01-15', lastOrder: 'heute' },
  { id: 2, name: 'Burger House', address: 'Marktplatz 7', city: 'München', status: 'aktiv', monthlyRevenue: 6230, commission: 62.30, joinedDate: '2024-02-20', lastOrder: 'gestern' },
  { id: 3, name: 'Sushi World', address: 'Hafenstr. 45', city: 'Hamburg', status: 'pending', monthlyRevenue: 0, commission: 0, joinedDate: '2024-03-28', lastOrder: '-' },
];

// ============================================
// LEVEL SYSTEM MOCK DATA
// ============================================

export const MOCK_LEVELS: Level[] = [
  { level: 1, name: 'Starter', minPoints: 0, maxPoints: 100, bonus: 0, perks: ['Basis-Provisionen', 'Referral-Link'], unlocked: true },
  { level: 2, name: 'Bronze', minPoints: 100, maxPoints: 250, bonus: 500, perks: ['Erhöhte Provisionen (+5%)', 'Marketing-Material'], unlocked: true },
  { level: 3, name: 'Silber', minPoints: 250, maxPoints: 500, bonus: 1000, perks: ['Erhöhte Provisionen (+10%)', 'Priorisierter Support'], unlocked: true },
  { level: 4, name: 'Gold', minPoints: 500, maxPoints: 1000, bonus: 2500, perks: ['Erhöhte Provisionen (+15%)', 'VIP Events', 'Persönlicher Ansprechpartner'], unlocked: false },
  { level: 5, name: 'Platin', minPoints: 1000, maxPoints: 2500, bonus: 5000, perks: ['Erhöhte Provisionen (+20%)', 'Exklusive Deals', 'Quartals-Bonus'], unlocked: false },
  { level: 6, name: 'Diamant', minPoints: 2500, maxPoints: 99999, bonus: 10000, perks: ['Maximum Provisionen (+25%)', 'Top-Partner Status', 'Individuelle Konditionen'], unlocked: false },
];

export const MOCK_USER_PROGRESS = {
  currentLevel: 3,
  currentPoints: 366,
  nextLevelPoints: 500,
  totalEarned: 5340.20,
  conversions: 18,
  bonusesCollected: 1500,
};

// ============================================
// MARKETING ASSETS MOCK DATA
// ============================================

export const MOCK_MARKETING_BANNERS: MarketingAsset[] = [
  { id: 1, name: 'Leaderboard Banner (728x90)', format: 'PNG', size: '45KB', category: 'banner' },
  { id: 2, name: 'Skyscraper Banner (160x600)', format: 'PNG', size: '62KB', category: 'banner' },
  { id: 3, name: 'Medium Rectangle (300x250)', format: 'PNG', size: '38KB', category: 'banner' },
  { id: 4, name: 'Mobile Banner (320x50)', format: 'PNG', size: '22KB', category: 'banner' },
  { id: 5, name: 'Social Media Post (1080x1080)', format: 'PNG', size: '156KB', category: 'banner' },
];

export const MOCK_MARKETING_DOCUMENTS: MarketingAsset[] = [
  { id: 1, name: 'Verkaufsargumente Übersicht', format: 'PDF', size: '890KB', category: 'document' },
  { id: 2, name: 'Provisionsmodell Erklärt', format: 'PDF', size: '420KB', category: 'document' },
  { id: 3, name: 'Restaurant Onboarding Guide', format: 'PDF', size: '1.2MB', category: 'document' },
  { id: 4, name: 'FAQ für Restaurants', format: 'PDF', size: '340KB', category: 'document' },
];

export const MOCK_MARKETING_TEMPLATES: MarketingAsset[] = [
  { id: 1, name: 'E-Mail Vorlage: Erstansprache', format: 'HTML', size: '12KB', category: 'template' },
  { id: 2, name: 'E-Mail Vorlage: Follow-Up', format: 'HTML', size: '10KB', category: 'template' },
  { id: 3, name: 'Telefonskript', format: 'DOCX', size: '28KB', category: 'template' },
  { id: 4, name: 'Social Media Textvorlagen', format: 'TXT', size: '8KB', category: 'template' },
];

export const MOCK_MARKETING_VIDEOS: MarketingAsset[] = [
  { id: 1, name: 'SpeiseGuru Produktvideo', format: 'MP4', duration: '2:30', category: 'video' },
  { id: 2, name: 'Top-Partner Interview', format: 'MP4', duration: '5:15', category: 'video' },
  { id: 3, name: 'Sales Pitch Training', format: 'MP4', duration: '12:00', category: 'video' },
];

export const MOCK_REFERRAL_LINKS = {
  standard: 'speiseguru.de/ref/martink',
  landing: 'speiseguru.de/partner/martink',
  demo: 'speiseguru.de/demo?ref=martink',
};

// ============================================
// SETTINGS MOCK DATA
// ============================================

export const MOCK_PROFILE: AffiliateProfile = {
  firstName: 'Martin',
  lastName: 'Kowalski',
  email: 'martin.kowalski@example.com',
  phone: '+49 172 1234567',
  company: 'MK Solutions GmbH',
  vatId: 'DE123456789',
  street: 'Musterstraße 123',
  city: 'Berlin',
  zip: '10115',
  country: 'Deutschland',
  referralCode: 'martink',
};

export const MOCK_PAYMENT_SETTINGS: PaymentSettings = {
  method: 'sepa',
  iban: 'DE89370400440532013000',
  bic: 'COBADEFFXXX',
  accountHolder: 'Martin Kowalski',
  paypalEmail: '',
  minPayout: '50',
};

export const MOCK_NOTIFICATION_SETTINGS: NotificationSettings = {
  emailNewLead: true,
  emailConversion: true,
  emailPayout: true,
  emailNewsletter: false,
  pushEnabled: true,
  pushNewLead: true,
  pushConversion: true,
};

// ============================================
// HELPERS
// ============================================

export const getLeadStatusConfig = (status: LeadStatus) => {
  const config: Record<LeadStatus, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    neu: { variant: 'info', label: 'Neu' },
    kontaktiert: { variant: 'neutral', label: 'Kontaktiert' },
    interessiert: { variant: 'warning', label: 'Interessiert' },
    verhandlung: { variant: 'warning', label: 'Verhandlung' },
    gewonnen: { variant: 'success', label: 'Gewonnen' },
    verloren: { variant: 'error', label: 'Verloren' },
  };
  return config[status];
};

export const getCommissionStatusConfig = (status: CommissionStatus) => {
  const config: Record<CommissionStatus, { variant: 'success' | 'warning' | 'info'; label: string }> = {
    ausstehend: { variant: 'warning', label: 'Ausstehend' },
    freigegeben: { variant: 'success', label: 'Freigegeben' },
    ausgezahlt: { variant: 'info', label: 'Ausgezahlt' },
  };
  return config[status];
};

export const getPayoutStatusConfig = (status: PayoutStatus) => {
  const config: Record<PayoutStatus, { variant: 'success' | 'warning' | 'error' | 'info'; label: string }> = {
    pending: { variant: 'warning', label: 'Ausstehend' },
    processing: { variant: 'info', label: 'In Bearbeitung' },
    completed: { variant: 'success', label: 'Abgeschlossen' },
    failed: { variant: 'error', label: 'Fehlgeschlagen' },
  };
  return config[status];
};

export const getRestaurantStatusConfig = (status: RestaurantStatus) => {
  const config: Record<RestaurantStatus, { variant: 'success' | 'warning' | 'error'; label: string }> = {
    aktiv: { variant: 'success', label: 'Aktiv' },
    pending: { variant: 'warning', label: 'Pending' },
    inaktiv: { variant: 'error', label: 'Inaktiv' },
  };
  return config[status];
};

export const getCommissionTypeLabel = (type: CommissionType) => {
  const map: Record<CommissionType, string> = {
    einmalig: 'Einmalig',
    wiederkehrend: 'Wiederkehrend',
    bonus: 'Bonus',
  };
  return map[type];
};

export const getLeadSourceLabel = (source: LeadSource) => {
  const map: Record<LeadSource, string> = {
    website: 'Website',
    referral: 'Empfehlung',
    'cold-call': 'Kaltakquise',
    event: 'Event',
    social: 'Social Media',
  };
  return map[source];
};
